import React, { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import sortBy from "lodash/sortBy";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Close from "@mui/icons-material/Close";
import { useQuery } from "@apollo/client/react";
import CircularProgress from "@mui/material/CircularProgress";

import useMutation from "src/hooks/useMutation";
import { TRANSFER_SHIPMENT_MUTATION } from "src/constants/graphql/mutations";
import { CLIENT_PROJECT_OPTIONS_QUERY } from "src/constants/graphql/queries";
import EntitySelect from "src/components/EntitySelect";
import useNotification from "src/hooks/notification";
import useAuth from "src/hooks/useAuth";
import ReceivingAssistantAttentionModal from "./ReceivingAssistantAttentionModal";
import {
  buildDuplicateProjectNameCounts,
  getProjectClientLabel,
  getProjectOptionLabel,
} from "src/utils/projectOptionLabels";
import { getActiveProjectsForClients } from "src/utils/clientProjectOptions";

export default function TransferShipmentModal({ shipment, onTransferSuccess, onClose }) {
  const { notifySuccess, notifyError } = useNotification();
  const { user } = useAuth();
  const { settings } = user.company;
  const filterField2 = settings?.filter_field2;
  const warehouseDropdownEnabled = settings.warehouse_id_dropdown_menu;
  const showClientNameInProjectList = Boolean(settings.display_client_name_email_notification);
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState(null);
  const clientId = shipment?.project?.client?.id;
  const [selectedClient, setSelectedClient] = useState(clientId || null);
  const [showAttentionModal, setShowAttentionModal] = useState(false);
  const [transferShipment, { loading }] = useMutation(TRANSFER_SHIPMENT_MUTATION);
  const { data: { clients = [] } = {} } = useQuery(CLIENT_PROJECT_OPTIONS_QUERY);

  const clientList = sortBy(
    clients
      .filter(client => !client.disabled)
      .map(({ id, name }) => ({ id, name }))
      .filter(v => user.clientIds.includes(v.id)),
    "name"
  );

  const projectList = useMemo(
    () => getActiveProjectsForClients(clients, !warehouseDropdownEnabled ? selectedClient : null),
    [clients, selectedClient, warehouseDropdownEnabled]
  );
  const duplicateNameCounts = useMemo(
    () => buildDuplicateProjectNameCounts(projectList),
    [projectList]
  );

  const handleOpenReceivingWizard = useCallback(() => {
    if (!projectId) return;
    const [, , , tab] = window.location.pathname.split("/");
    const params = new URLSearchParams(window.location.search);
    params.set("step", "shipment");
    params.set("receiving", "true");
    params.set("editShipment", "true");
    if (shipment?.shipmentId) params.set("shipmentId", shipment.shipmentId);
    if (shipment?.id) params.set("id", shipment.id);
    navigate({ pathname: `/projects/${projectId}/${tab}`, search: params.toString() });
  }, [navigate, projectId, shipment, onClose]);

  const handleTransferShipment = useCallback(() => {
    if (!projectId) {
      notifyError("Please select a project");
      return;
    }

    transferShipment({
      variables: {
        shipmentId: shipment?.id,
        projectId,
      },
      onSuccess: () => {
        notifySuccess("Shipment successfully transferred");

        if (onTransferSuccess) {
          console.log("Calling refetch from onTransferSuccess");
          onTransferSuccess();
        }

        setShowAttentionModal(true);
      },
      onFailure: () => {
        notifyError("Something went wrong during shipment transfer");
      },
    });
  }, [
    shipment,
    projectId,
    transferShipment,
    onClose,
    onTransferSuccess,
    notifyError,
    notifySuccess,
  ]);

  if (showAttentionModal) {
    return (
      <ReceivingAssistantAttentionModal
        open={showAttentionModal}
        onClose={onClose}
        onNext={handleOpenReceivingWizard}
      />
    );
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { maxWidth: 540 } }}>
      <DialogTitle>
        <Box component="span" display="flex" flexDirection="column" alignItems="center">
          <Typography fontSize={22} fontWeight={500} color="text.primary" align="center">
            Transfer Shipment
          </Typography>
          <Typography
            variant="body1"
            component="span"
            color="textSecondary"
            sx={{ mt: 1, fontWeight: "normal" }}
          >
            Select the destination {filterField2}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary" component="span">
            Shipment ID: {shipment?.shipmentId}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {!warehouseDropdownEnabled && (
            <Grid size={{ xs: 12, md: 6 }}>
              <EntitySelect
                fullWidth
                label={settings?.filter_field1}
                value={selectedClient}
                suggestions={clientList}
                onChange={clientItem => {
                  const id = clientItem?.id ? clientItem.id : null;
                  setSelectedClient(id);
                  setProjectId(null);
                }}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, md: warehouseDropdownEnabled ? 12 : 6 }}>
            <Box mt={1} />
            <EntitySelect
              fullWidth
              label={filterField2}
              value={projectId}
              suggestions={projectList}
              getOptionLabel={option =>
                getProjectOptionLabel(
                  option,
                  duplicateNameCounts,
                  showClientNameInProjectList
                )
              }
              renderOption={(props, option) => {
                const optionProps = { ...props };
                delete optionProps.key;
                const clientLabel = getProjectClientLabel(
                  option,
                  duplicateNameCounts,
                  showClientNameInProjectList
                );
                return (
                  <li {...optionProps} key={option.id}>
                    {option.name}
                    {clientLabel ? (
                      <Typography component="span" ml={0.5} color="text.secondary" fontSize={14}>
                        {" - "}
                        {clientLabel}
                      </Typography>
                    ) : null}
                  </li>
                );
              }}
              onChange={projectItem => {
                setProjectId(projectItem.id);
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
        <Button
          disabled={!projectId || loading}
          variant="contained"
          color="primary"
          onClick={handleTransferShipment}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          Transfer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
